import axios from "axios";

const getHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token && { auth: token }),
  };
};

// Prevent multiple init calls (React StrictMode, re-renders)
let initInProgress = false;

export const notificationService = {
  isSupported: () => {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  },

  registerSW: async () => {
    if (!("serviceWorker" in navigator)) return null;
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");
      // Wait for the SW to be ready
      await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
      return null;
    }
  },

  requestPermission: async () => {
    if (!("Notification" in window)) return "denied";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    return await Notification.requestPermission();
  },

  subscribeToPush: async (registration) => {
    try {
      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // No subscription yet — create one
        const { data } = await axios.get("/api/push-public-key", {
          headers: getHeaders(),
        });

        const vapidPublicKey = data.publicKey;
        if (!vapidPublicKey) {
          console.error("No VAPID public key from server");
          return null;
        }

        const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // Always send the current subscription to backend so it stays fresh
      const username = localStorage.getItem("fullName") || localStorage.getItem("username") || "";
      await axios.post(
        "/api/push-subscribe",
        {
          subscription: subscription.toJSON(),
          username,
        },
        { headers: getHeaders() }
      );

      console.log("Push subscription synced to server");
      return subscription;
    } catch (error) {
      // If subscription expired, unsubscribe and retry once
      if (error.name === "InvalidStateError" || error.message?.includes("expired")) {
        try {
          const oldSub = await registration.pushManager.getSubscription();
          if (oldSub) await oldSub.unsubscribe();
          return await notificationService.subscribeToPush(registration);
        } catch (retryError) {
          console.error("Failed to resubscribe:", retryError);
        }
      }
      console.error("Failed to subscribe to push:", error);
      return null;
    }
  },

  init: async () => {
    // Guard against duplicate calls
    if (initInProgress) return;
    initInProgress = true;

    try {
      if (!notificationService.isSupported()) {
        console.log("Push notifications not supported on this browser");
        return;
      }

      const registration = await notificationService.registerSW();
      if (!registration) return;

      const permission = await notificationService.requestPermission();
      if (permission !== "granted") {
        console.log("Notification permission denied");
        return;
      }

      await notificationService.subscribeToPush(registration);
    } finally {
      initInProgress = false;
    }
  },
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
